<template>
    <div class="kpi"
         @click="!disabled && $emit('click')"
         :id="`kpi${id}`"
         :class="{'not-selected': !selected, selected: selected, disabled: disabled, first: isFirst}">
      <Tooltip class="kpi-tooltip">
                  <span class="tooltip-title">
                      {{tooltip.metricData.title}}
                  </span>
                  <span v-if="tooltip.metricData.text">
                        <br>
                        {{tooltip.metricData.text}}
                      </span>
                  <span v-if="!noData && tooltip.trendData">
                      <hr>
                      <span class="tooltip-title">
                        {{tooltip.trendData.title}}
                      </span>
                      <span v-if="tooltip.trendData.text" class="trend-data-text">
                        <br>
                        {{tooltip.trendData.text}}
                      </span>
                  </span>
      </Tooltip>
        <span class="kpi-header">
          {{kpiData.name}}
        </span>
        <span class="kpi-header-suffix">
            {{kpiData.suffix | parentheses}}
        </span>
        <div class="kpi-value">
          <span v-if="noData" class="no-data">No Data</span>
          <span v-else id="kpiValue">
            {{symbol.prefix}} {{ value | numberCollapser}} {{symbol.suffix}}
            <span class="kpi-trend-icon">
              <IconBase
                  v-if="!isToday && hasPreviousValue"
                  :style="trend.style"
                  width="12"
                  height="12"
                  iconName="trendsArrowIcon"
                  :iconColor="trend.color"></IconBase>
            </span>
          </span>
        </div>
    </div>
</template>

<script>

import consts from 'services/metaData/consts';
import currencySymbolMap from 'services/utils/currencySymbolMap';
import IconBase from 'components/IconBase.vue';
import numberCollapser from 'services/filters/numberCollapser';
import parentheses from 'services/filters/parentheses';
import Tooltip from 'components/Tooltip/ClosableTooltip.vue';
import kpiSymbolMapping from 'monetize/reports/utils/kpiSymbolMapping';
import kpisTextMapping from 'monetize/reports/utils/kpisTextMapping';

export default {
    name: 'KPI',
    components: { IconBase, Tooltip },
    props: {
        value: Number,
        previousValue: Number,
        id: String,
        isToday: Boolean,
        isFirst: Boolean,
        selected: Boolean,
        disabled: Boolean,
        currency: {
            type: String,
            default: 'USD'
        },
        previousTimeFrame: String
    },
    data() {
        return {
            consts,
            closeIcon: {
                color: consts.COLORS.LIGHT_700,
                size: 10
            }
        };
    },
    computed: {
        hasPreviousValue() {
            return Boolean(this.previousValue) || this.previousValue === 0;
        },
        trend() {
            let style;
            let tooltipTitle;
            let color;
            let trendTooltip;
            const { trendRatiosUp, trendRatiosDown } = this.getTrendRatios(this.id);
            const trendUpColor = '#00CF7E';
            const trendSameColor = '#FCBC2A';
            if (!this.previousValue || this.value / this.previousValue > trendRatiosUp) {
                tooltipTitle = 'Trending Up';
                trendTooltip = 'up';
                color = trendUpColor;
            } else if (this.value / this.previousValue < trendRatiosDown) {
                tooltipTitle = 'Trending Down';
                trendTooltip = 'down';
                color = consts.COLORS.NEGATIVE_500;
                style = 'transform: rotate(180deg)';
            } else {
                tooltipTitle = 'No Change';
                trendTooltip = 'unchanged';
                color = trendSameColor;
                style = 'transform: rotate(90deg)';
            }
            return {
                style, color, tooltipTitle, trendTooltip
            };
        },
        noData() {
            return typeof this.value === 'undefined' || !this.kpiData;
        },
        currencySymbol() {
            return currencySymbolMap[this.currency];
        },
        symbolMapping() {
            return kpiSymbolMapping(this.currencySymbol);
        },
        symbol() {
            const kpiSymbol = this.symbolMapping[this.id];
            let prefix;
            let suffix;

            if (kpiSymbol && kpiSymbol.location === 'pre') {
                prefix = kpiSymbol.symbol;
            }

            if (kpiSymbol && kpiSymbol.location === 'post') {
                suffix = kpiSymbol.symbol;
            }

            return { prefix, suffix };
        },
        kpiData() {
            return kpisTextMapping[this.id];
        },
        tooltip() {
            const metricData = {
                title: this.kpiData.name,
                text: this.kpiData.tooltip
            };
            const trendData = this.hasPreviousValue && {
                title: this.trend.tooltipTitle,
                text: `${this.kpiData.name} is ${this.trend.trendTooltip} compared to ${this.previousTimeFrame}`
            };
            return { metricData, trendData };
        }
    },
    methods: {
        getTrendRatios(kpi) {
            const defaultTrendPercent = 5;
            const kpiData = kpisTextMapping[kpi];
            const trendPercent = kpiData?.trendPercent || defaultTrendPercent;
            const trendPercentDecimal = trendPercent / 100;
            const trendRatiosUp = 1 + trendPercentDecimal;
            const trendRatiosDown = 1 - trendPercentDecimal;

            return { trendRatiosUp, trendRatiosDown };
        }
    },
    filters: {
        numberCollapser,
        parentheses
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/input";
    @import "~styles/screens";

    .kpi {
      position: relative;
      vertical-align: middle;
      padding: 10px 15px;
      box-shadow: 1px 0 6px 0 rgba(77, 80, 95, 0.22);
      background-color: $light-200;
      color: $dark-300;
      cursor: pointer;
      //margin-top: 2px;

      //border-top: $border;

      .kpi-value {
        @include font(1);
        line-height: 30px;
        overflow: hidden;

        .no-data {
          @include font(3);
        }
      }


      .kpi-header {
        @include font(4);
        white-space: nowrap;
      }

      .kpi-header-suffix {
        @include font(5);
        font-weight: normal;
        @media #{$tabletAndBelow}{
          display: none;
        }
      }

      .kpi-tooltip {
        position: absolute;
        right: 13px;
        @media #{"only screen and (max-width:#{1050px})"}{
          right: 10px;
        }
      }

      .kpi-trend-icon {
        margin-left: 6px;
        margin-bottom: 2px;
        @media #{$tabletAndBelow}{
          display: none;
        }
      }
    }

    .kpi-wrapper {
      background-color: $light-500;
      padding-top: 1px;
    }

    .not-selected {
      background-color: $light-200;
      box-shadow: none;
      border-left: 1px solid $white;
      padding-top: 11px;

        &:hover {
          background-color: $light-500;
        }
      }

    .selected {
      z-index: getZIndexLayerOffset(normal, 1);
      margin-top: 1px;
      background-color: $white;
      border-top-left-radius: 1px;
    }

    .disabled {
        cursor: default;
        background-color: $light-100;
        color: $light-700;
        box-shadow: none;

        &:hover {
          background-color: $light-100;
        }
      }

    .first {
      border-left: none;
    }

    .tooltip-title{
      @include font(6);
      margin-bottom: 10px;
    }

    hr {
      margin-top: 10px;
      margin-bottom: 10px;
    }

    .trend-data-text {
        white-space: pre-line;
    }
</style>
